import { LOCAL_STORAGE_KEYS } from '../../utils/constants';

type SignatureIndexedRecord = {
    id?: string;
    signatureData?: string;
    signatureBlob?: Blob;
    equipmentId?: string | null;
    unitId?: string | null;
    equipmentName?: string | null;
};

/**
 * Normalize a thrown signature-storage value into a real `Error`.
 *
 * `react-indexed-db-hook` rejects with the raw IndexedDB `error` Event (an
 * `IDBRequest` event), not an `Error`. Passing that straight to
 * `Sentry.captureException` produces an opaque "Event `Event` (type=error)"
 * issue with no actionable message. This pulls the underlying DOMException
 * (e.g. QuotaExceededError, UnknownError) out of the event so Sentry can group
 * and name the failure.
 */
export function toSignatureError(value: unknown, context: string): Error {
    if (value instanceof Error) {
        return value;
    }

    // IndexedDB request error events: { target: IDBRequest { error: DOMException } }
    const target = (value as { target?: { error?: unknown } } | null)?.target;
    const requestError = (target as { error?: unknown } | null)?.error;
    if (requestError instanceof Error) {
        const name = requestError.name || 'IndexedDBError';
        const normalized = new Error(`${context}: ${name}: ${requestError.message}`);
        normalized.name = name;
        return normalized;
    }

    const type = (value as { type?: unknown } | null)?.type;
    if (typeof type === 'string') {
        return new Error(`${context}: IndexedDB request ${type} event`);
    }

    if (typeof value === 'string') {
        return new Error(`${context}: ${value}`);
    }

    return new Error(context);
}

export function getSavedSignatureLocalStorageKey(companyId: string): string {
    return `saved_signature_${companyId}`;
}

export function readSavedSignatureData(companyId: string | undefined): string | null {
    if (!companyId) {
        return null;
    }

    return localStorage.getItem(getSavedSignatureLocalStorageKey(companyId));
}

export function readCachedSignatureStoragePath(): string | null {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH);
}

export function resolveSignatureDataFromRecord(record: SignatureIndexedRecord | null | undefined): string | null {
    if (!record) {
        return null;
    }

    if (typeof record.signatureData === 'string' && record.signatureData.length > 0) {
        return record.signatureData;
    }

    return null;
}
